import {Editor} from '@tinymce/tinymce-react'
import {useRef} from 'react'

const KEY = 'xu0w2376ad020opfza3jbzs9cfxaml43fnzvfazg18ketj2w'

const allowedStyles = new Set([
    'font-family',
    'font-size',
    'font-weight',
    'font-style',
    'color',
    'background-color',
    'text-align',
    'text-decoration',
    'line-height',
    'letter-spacing',
])

const RichTextInput = ({value, onChange, language}) => {
    const editorRef = useRef(null)
    const currentHtmlRef = useRef(value)

    const cleanHtml = (html = '') => {
        return (
            html
                // Remove Word/Office tags
                .replace(/<!--\[if.*?endif\]-->/gi, '')
                .replace(/<(?:o|w|m):[^>]+>/gi, '')
                .replace(/<\/(?:o|w|m):[^>]+>/gi, '')
                // Remove classes & data attributes
                .replace(/\sclass="[^"]*"/gi, '')
                .replace(/\sdata-[^=]+="[^"]*"/gi, '')
                // Filter styles, keep only allowed ones
                .replace(/style="([^"]*)"/gi, (_, styles) => {
                    const filtered = styles
                        .split(';')
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .map((s) => {
                            const [prop, value] = s
                                .split(':')
                                .map((x) => x.trim())
                            return allowedStyles.has(prop.toLowerCase()) &&
                                value
                                ? `${prop}:${value}`
                                : null
                        })
                        .filter(Boolean)
                        .join(';')
                    return filtered ? `style="${filtered}"` : ''
                })
                .trim()
        )
    }

    const customSetup = (editor) => {
        editor.ui.registry.addButton('customImageButton', {
            icon: 'image',
            tooltip: 'Insert image',
            onAction: () => {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = 'image/*'

                input.onchange = (e) => {
                    const file = e.target.files[0]
                    if (!file) return

                    const reader = new FileReader()
                    reader.onload = () => {
                        editor.insertContent(
                            `<img src="${reader.result}" alt="${file.name}" style="max-width: 100%;"/>`
                        )
                    }
                    reader.readAsDataURL(file)
                }

                input.click()
            },
        })
    }

    return (
        <Editor
            apiKey={KEY}
            onInit={(_evt, editor) => (editorRef.current = editor)}
            initialValue={value}
            onEditorChange={(html) => {
                currentHtmlRef.current = html
            }}
            onBlur={() => {
                onChange(cleanHtml(currentHtmlRef.current))
            }}
            init={{
                language: language === 'he' ? 'he' : 'en',
                directionality: language === 'he' ? 'rtl' : 'ltr',
                height: 400,
                menubar: false,
                plugins: ['lists', 'link', 'image', 'paste', 'wordcount'],
                toolbar:
                    'undo redo | blocks | fontfamily fontsize | ' +
                    'bold underline italic | forecolor backcolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | link customImageButton',
                font_family_formats:
                    'Arial=arial,helvetica,sans-serif; ' +
                    'Arial Black=arial black,avant garde; ' +
                    'Comic Sans MS=comic sans ms,sans-serif; ' +
                    'Courier New=courier new,courier; ' +
                    'Georgia=georgia,palatino; ' +
                    'Helvetica=helvetica; ' +
                    'Impact=impact,chicago; ' +
                    'Tahoma=tahoma,arial,helvetica,sans-serif; ' +
                    'Times New Roman=times new roman,times; ' +
                    'Trebuchet MS=trebuchet ms,geneva; ' +
                    'Verdana=verdana,geneva; ' +
                    'David=david,serif; ' +
                    'Miriam=miriam,sans-serif; ' +
                    'Frank Ruhl Libre=frank ruhl libre,serif',
                content_style: `
                    @import url('https://fonts.googleapis.com/css2?family=Frank+Ruhl+Libre:wght@300;400;500;700&display=swap');
                    body {
                        font-size: 16px;
                        ${
                            language === 'he'
                                ? 'direction: rtl; text-align: right;'
                                : ''
                        }
                    }
                    a { color: #0077cc; text-decoration: none; cursor: pointer; }
                `,
                setup: customSetup,
            }}
        />
    )
}

export default RichTextInput
